import React, { useContext } from 'react';
import PrivacyButton, { isKetchLink, cleanCountryLink } from './PrivacyButton';
import {
    KontentRichText,
    KontentStringValue,
} from '../../../types/KontentProps';
//@ts-ignore
import { RichTextElement } from '@kontent-ai/gatsby-components';
import { DomElement, domToReact } from 'html-react-parser';
import QuotedText from '../QuotedText';
import Headline from '../Headline';
import * as headlineStyles from '../Headline/headline.module.css';
import {
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    imagePreset,
    buildKontentSrcSet,
    getDisplayDimensions,
} from './DataUtils';
import LazyImage from './LazyImage';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { prefixWithLang } from '../../global/countries';
import { withTrailingSlash } from '../../utils/trailingSlash';

import RteComponent from '../RteComponent';
import BlogContentRendering from '../BlogContentRendering';
import VideoPlayer from './VideoPlayer';

type Image = {
    url: string;
    description?: string;
    name?: string;
    width?: number;
    height?: number;
};

type Link = {
    codename: string;
    url_slug: string;
    type: string;
    link_id: string;
};

interface KontentItemComponent {
    __typename: string;
    elements: any;
    system: {
        id?: string;
        type: string;
        codename?: string;
    };
}

interface PagePaths {
    [key: string]: string;
}

const RichTextContainer = ({
    richText,
    pagePaths,
    preferredLanguage,
    giveID = false,
    panelName = undefined,
    wrapperStyle,
    wrapperClassName,
    elementCodename,
    preserveHeadingSemantics = false,
    preservePrivacyLinks = false,
}: {
    richText: KontentRichText;
    pagePaths: PagePaths;
    preferredLanguage: string;
    giveID?: boolean | undefined;
    panelName?: KontentStringValue | undefined;
    wrapperStyle?: React.CSSProperties;
    wrapperClassName?: string;
    elementCodename?: string;
    preserveHeadingSemantics?: boolean;
    preservePrivacyLinks?: boolean;
}) => {
    if (!richText) return null;
    return (
        <div
            className={wrapperClassName}
            style={{
                clear: 'both',
                ...(!wrapperClassName ? { overflow: 'auto' } : {}),
                ...wrapperStyle,
            }}
            {...(elementCodename
                ? { 'data-kontent-element-codename': elementCodename }
                : {})}
        >
            <RichTextElement
                value={richText.value?.replace(/&nbsp;/g, ' ')}
                images={richText.images}
                resolveImage={(image: Image) => {
                    return (
                        <LazyImage
                            src={optimizeKontentImageUrl(
                                image?.url,
                                imagePreset.inline.large
                            )}
                            srcSet={buildKontentSrcSet(
                                image?.url,
                                imagePreset.inline.large.w,
                                {
                                    fm: 'webp' as const,
                                    q: imagePreset.inline.large.q,
                                }
                            )}
                            sizes="(max-width: 768px) 100vw, 50vw"
                            alt={
                                image?.description
                                    ? image?.description
                                    : image?.name || ''
                            }
                            {...getDisplayDimensions(
                                image?.width,
                                image?.height,
                                imagePreset.inline.large.w
                            )}
                            style={{ width: '100%', height: 'auto' }}
                        />
                    );
                }}
                links={richText.links}
                resolveLink={(link: Link, domNode: DomElement) => {
                    let path = link?.url_slug;

                    if (pagePaths) {
                        path = pagePaths[link?.url_slug];
                    }

                    const linkHref = withTrailingSlash(
                        prefixWithLang(path, preferredLanguage)
                    );

                    if (domNode?.children && path) {
                        return (
                            <a
                                href={linkHref}
                                id={
                                    giveID && panelName?.value
                                        ? domToReact(domNode.children)?.type ===
                                          'strong'
                                            ? panelName.value.replace(
                                                  / /g,
                                                  '-'
                                              ) +
                                              '__' +
                                              domToReact(domNode.children)
                                                  ?.props?.children?.toString()
                                                  ?.replace(/ /g, '-')
                                            : panelName.value?.replace(
                                                  / /g,
                                                  '-'
                                              ) +
                                              '__' +
                                              domToReact(domNode.children)
                                                  ?.toString()
                                                  ?.replace(/ /g, '-')
                                        : undefined
                                }
                            >
                                {domToReact(domNode.children)}
                            </a>
                        );
                    }
                }}
                linkedItems={richText.modular_content}
                resolveLinkedItem={(
                    linkedItem: KontentItemComponent,
                    domNode: any
                ) => {
                    const isComponent =
                        domNode?.attribs?.['data-type'] === 'component';
                    const smartLinkAttr = isComponent
                        ? {
                              'data-kontent-component-id':
                                  domNode?.attribs?.['data-id'],
                          }
                        : { 'data-kontent-item-id': linkedItem.system?.id };

                    const wrapWithSmartLink = (content: React.ReactNode) => (
                        <div style={{ display: 'contents' }} {...smartLinkAttr}>
                            {content}
                        </div>
                    );

                    switch (linkedItem.system.type) {
                        case 'component___quoted_text':
                            return wrapWithSmartLink(
                                <QuotedText {...linkedItem.elements} />
                            );
                        case 'component___headline':
                            return wrapWithSmartLink(
                                <Headline
                                    kontentItemId={linkedItem.system?.id}
                                    {...linkedItem.elements}
                                    overrideStyles={{
                                        margin: 'auto',
                                        maxWidth: '100%',
                                        paddingLeft: '0',
                                        paddingRight: '0',
                                    }}
                                />
                            );
                        case 'component___rte':
                            return wrapWithSmartLink(
                                <RteComponent
                                    {...linkedItem.elements}
                                    overrideStyles={{
                                        margin: 'auto',
                                        maxWidth: '100%',
                                        paddingLeft: '0',
                                        paddingRight: '0',
                                    }}
                                />
                            );
                        case 'component___blog_content':
                            return wrapWithSmartLink(
                                <BlogContentRendering
                                    {...linkedItem.elements}
                                />
                            );
                        case 'rte___floating_image':
                            const imgData = getMediaData(
                                linkedItem.elements.image
                            );
                            const float = getMultiChoice(
                                linkedItem.elements.image_float_position
                            );
                            let padding =
                                float === 'left'
                                    ? '0 2rem 0 0'
                                    : float && '0 0 0 2rem';

                            const authorStyles = {
                                float: float,
                                width: 'auto',
                                maxWidth: '100%',
                                padding: padding || '1rem',
                                clear: 'both',
                            };
                            return wrapWithSmartLink(
                                <LazyImage
                                    src={optimizeKontentImageUrl(
                                        imgData?.url,
                                        imagePreset.inline.small
                                    )}
                                    srcSet={buildKontentSrcSet(
                                        imgData?.url,
                                        imagePreset.inline.small.w,
                                        {
                                            fm: 'webp' as const,
                                            q: imagePreset.inline.small.q,
                                        }
                                    )}
                                    sizes="(max-width: 768px) 50vw, 25vw"
                                    alt={
                                        imgData?.description ||
                                        imgData?.name ||
                                        ''
                                    }
                                    {...getDisplayDimensions(
                                        imgData?.width,
                                        imgData?.height,
                                        imagePreset.inline.small.w
                                    )}
                                    //@ts-ignore
                                    style={authorStyles}
                                />
                            );
                        case 'media___image':
                            const medImageData =
                                linkedItem.elements?.file?.value[0];

                            return wrapWithSmartLink(
                                <LazyImage
                                    src={optimizeKontentImageUrl(
                                        medImageData?.url,
                                        imagePreset.inline.large
                                    )}
                                    srcSet={buildKontentSrcSet(
                                        medImageData?.url,
                                        imagePreset.inline.large.w,
                                        {
                                            fm: 'webp' as const,
                                            q: imagePreset.inline.large.q,
                                        }
                                    )}
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                    alt={
                                        medImageData?.description ||
                                        medImageData?.name ||
                                        ''
                                    }
                                    {...getDisplayDimensions(
                                        medImageData?.width,
                                        medImageData?.height,
                                        imagePreset.inline.large.w
                                    )}
                                    style={{ width: '100%', height: 'auto' }}
                                />
                            );
                        case 'media___general_video':
                            const src =
                                linkedItem?.elements?.youtube_url?.value;

                            return wrapWithSmartLink(<VideoPlayer src={src} />);
                        default:
                            return (
                                <p style={{ color: '#f06623' }}>
                                    <strong>{linkedItem.system.type}</strong>{' '}
                                    component not available in rich text
                                    element. To correct, add component to{' '}
                                    <code>RichText</code> function or return{' '}
                                    <code>ReferenceComponent</code> by default
                                    in{' '}
                                    <code>
                                        src/components/Global/RichText.tsx
                                    </code>
                                    .
                                </p>
                            );
                    }
                }}
                resolveDomNode={(domNode: DomElement, domToReact: Function) => {
                    if (domNode.name === 'a' && domNode.attribs?.href) {
                        if (isKetchLink(domNode.attribs.href)) {
                            if (preservePrivacyLinks) return;
                            return (
                                <PrivacyButton>
                                    {domToReact(domNode.children)}
                                </PrivacyButton>
                            );
                        }
                        domNode.attribs.href = withTrailingSlash(
                            cleanCountryLink(domNode.attribs.href)
                        );
                    }
                    if (domNode.name === 'h1') {
                        return preserveHeadingSemantics ? (
                            <h1
                                className={headlineStyles.h1}
                                style={{ margin: 0, fontWeight: 'normal' }}
                            >
                                {domToReact(domNode.children)}
                            </h1>
                        ) : (
                            <div className={headlineStyles.h1}>
                                {domToReact(domNode.children)}
                            </div>
                        );
                    }

                    if (domNode.name === 'h2') {
                        return preserveHeadingSemantics ? (
                            <h2
                                className={headlineStyles.h2}
                                style={{ margin: 0, fontWeight: 'normal' }}
                            >
                                {domToReact(domNode.children)}
                            </h2>
                        ) : (
                            <div className={headlineStyles.h2}>
                                {domToReact(domNode.children)}
                            </div>
                        );
                    }

                    if (domNode.name === 'h3') {
                        return preserveHeadingSemantics ? (
                            <h3
                                className={headlineStyles.h3}
                                style={{ margin: 0, fontWeight: 'normal' }}
                            >
                                {domToReact(domNode.children)}
                            </h3>
                        ) : (
                            <div className={headlineStyles.h3}>
                                {domToReact(domNode.children)}
                            </div>
                        );
                    }

                    if (domNode.name === 'h4') {
                        return preserveHeadingSemantics ? (
                            <h4
                                className={headlineStyles.h4}
                                style={{ margin: 0, fontWeight: 'normal' }}
                            >
                                {domToReact(domNode.children)}
                            </h4>
                        ) : (
                            <div className={headlineStyles.h4}>
                                {domToReact(domNode.children)}
                            </div>
                        );
                    }
                }}
            />
        </div>
    );
};

const RichText = (
    richText: KontentRichText,
    giveID?: boolean | undefined,
    panelName?: KontentStringValue | undefined,
    wrapperStyle?: React.CSSProperties,
    elementCodename?: string
) => {
    const pageMetadata = useContext(PageMetadataContext);
    const pagePaths = pageMetadata?.pagePaths;
    const preferredLanguage = pageMetadata.preferredLanguage || 'default';

    return (
        <RichTextContainer
            richText={richText}
            pagePaths={pagePaths}
            preferredLanguage={preferredLanguage}
            giveID={giveID}
            panelName={panelName}
            wrapperStyle={wrapperStyle}
            elementCodename={elementCodename}
        />
    );
};

export default RichText;

export interface RichTextElProps {
    richText: KontentRichText;
    giveID?: boolean | undefined;
    panelName?: KontentStringValue | undefined;
    wrapperStyle?: React.CSSProperties;
    wrapperClassName?: string;
    elementCodename?: string;
    preserveHeadingSemantics?: boolean;
    preservePrivacyLinks?: boolean;
}

const RichTextEl: React.FC<RichTextElProps> = ({
    richText,
    giveID = false,
    panelName = undefined,
    wrapperStyle,
    wrapperClassName,
    elementCodename,
    preserveHeadingSemantics = false,
    preservePrivacyLinks = false,
}) => {
    const pageMetadata = useContext(PageMetadataContext);
    const pagePaths = pageMetadata?.pagePaths;
    const preferredLanguage = pageMetadata.preferredLanguage || 'default';

    return (
        <RichTextContainer
            richText={richText}
            pagePaths={pagePaths}
            preferredLanguage={preferredLanguage}
            giveID={giveID}
            panelName={panelName}
            wrapperStyle={wrapperStyle}
            wrapperClassName={wrapperClassName}
            elementCodename={elementCodename}
            preserveHeadingSemantics={preserveHeadingSemantics}
            preservePrivacyLinks={preservePrivacyLinks}
        />
    );
};

export { RichTextEl };
